import { DatePipe, NgTemplateOutlet } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, ElementRef, Signal, computed, inject, input, signal, viewChild } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { AuditFieldLabelPipe } from './pipes/meeting-audit-field-label.pipe';
import { AuditFieldTooltipPipe } from './pipes/meeting-audit-field-tooltip.pipe';
import { InitialPipe } from '@app/shared/pipes/initial.pipe';
import { AuditValuePipe } from './pipes/meeting-audit-value.pipe';
import { ITXService } from '@app/shared/services/itx.service';
import { ProjectService } from '@app/shared/services/project.service';
import { copyToClipboard, exportToCSV, onImageError } from '@app/shared/utils/helper';
import { ExportDataComponent } from '@app/shared/components/export-data/export-data.component';
import { MeetingAuditFiltersPanelComponent } from './components/meeting-audit-filters-panel/meeting-audit-filters-panel.component';
import { MeetingAuditLog, MeetingAuditUser, AuditRegistrantInfo, AuditAttendeeInfo } from 'lfx-pcc';
import { MessageService } from 'primeng/api';
import { AvatarModule } from 'primeng/avatar';
import { ButtonModule } from 'primeng/button';
import { DialogService } from 'primeng/dynamicdialog';
import { PaginatorModule } from 'primeng/paginator';
import { PaginatorState } from 'primeng/paginator';
import { SkeletonModule } from 'primeng/skeleton';
import { TooltipModule } from 'primeng/tooltip';
import { Observable, catchError, map, of, startWith, switchMap, take } from 'rxjs';

import { AuditState, MeetingAuditTableData, ProcessedMeetingAuditLog, FilterState } from './interfaces/meeting-audit-log.interface';
import { processAuditLogs, applyFilters, groupAuditLogs, getColumnMap, getAvailableExportFields, isUrlAttribute } from './utils/meeting-audit-log.utils';

@Component({
  selector: 'lfx-meeting-audit-log',
  standalone: true,
  imports: [
    NgTemplateOutlet,
    ButtonModule,
    InitialPipe,
    AuditValuePipe,
    AuditFieldLabelPipe,
    AuditFieldTooltipPipe,
    AvatarModule,
    SkeletonModule,
    PaginatorModule,
    TooltipModule,
    DatePipe,
    MeetingAuditFiltersPanelComponent,
  ],
  templateUrl: './meeting-audit-log.component.html',
  styleUrl: './meeting-audit-log.component.scss',
  providers: [DatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MeetingAuditLogComponent {
  public scrollContainer = viewChild<ElementRef>('scrollContainer');

  // Injected services
  private itxService = inject(ITXService);
  private projectService = inject(ProjectService);
  private messageService = inject(MessageService);
  private datePipe = inject(DatePipe);
  private dialogService = inject(DialogService);

  // Inputs
  public meetingId = input<string | null>(null);
  public meetingTopic = input<string>('');
  public isPastMeeting = input<boolean>(false);

  // Mutable UI state signals
  public currentPage = signal<number>(0);
  public pageSize = signal<number>(50);
  public isFiltersVisible = signal<boolean>(false);

  // Filter selection signals (user-mutable)
  public dateFrom = signal<Date | null>(null);
  public dateTo = signal<Date | null>(null);
  public selectedAttributes = signal<{ name: string; displayName: string }[]>([]);
  public selectedUsers = signal<(MeetingAuditUser & { displayName?: string })[]>([]);
  public selectedActions = signal<string[]>([]);
  public selectedResources = signal<string[]>([]);
  public selectedRegistrants = signal<(AuditRegistrantInfo & { displayName?: string })[]>([]);
  public selectedAttendees = signal<(AuditAttendeeInfo & { displayName?: string })[]>([]);

  // Declarative audit state — loading/error/data derived from a single observable stream
  private readonly auditParams = computed(() => ({ id: this.meetingId(), isPast: this.isPastMeeting() }));
  private readonly auditState: Signal<AuditState> = this.initializeAuditState();
  public readonly loading = computed(() => this.auditState().loading);
  public readonly hasError = computed(() => this.auditState().error);
  private readonly auditData = computed(() => this.auditState().data);

  // Project
  public readonly projectName = computed(() => this.projectService.currentProjectSig()?.Name ?? '');

  // Audit logs derived from auditData
  public readonly auditLogs = computed(() => this.auditData()?.sortedAuditLogs ?? []);
  private readonly auditLogTableData = computed(() => this.auditData()?.auditLogTableData ?? []);

  // Filter options derived from auditData
  public readonly userOptions = computed(() => this.auditData()?.filterUsers ?? []);
  public readonly attributeOptions = computed(() => this.auditData()?.filterAttributes ?? []);
  public readonly actionOptions = computed(() => this.auditData()?.filterActions ?? []);
  public readonly resourceOptions = computed(() => this.auditData()?.filterResources ?? []);
  public readonly registrantOptions = computed(() => this.auditData()?.filterRegistrants ?? []);
  public readonly attendeeOptions = computed(() => this.auditData()?.filterAttendees ?? []);

  // Filtered and paginated data chain
  public readonly filteredAuditLogs = this.initializeFilteredAuditLogs();
  public readonly paginatedLogs = computed(() =>
    this.filteredAuditLogs().slice(this.currentPage() * this.pageSize(), (this.currentPage() + 1) * this.pageSize())
  );
  public readonly groupedPaginatedLogs = computed(() => groupAuditLogs(this.paginatedLogs()));
  public readonly paginatorFirst = computed(() => this.currentPage() * this.pageSize());

  // Filter state
  public readonly hasActiveFilters = this.initializeHasActiveFilters();
  public readonly activeFilterCount = this.initializeActiveFilterCount();

  // Constants
  public readonly fieldColumnTooltip = "Please hover over the field's name to see a detailed description.";
  public readonly rowsPerPageOptions = [10, 25, 50, 100];
  public readonly isUrlAttribute = isUrlAttribute;

  public readonly onImageError = onImageError;

  public onExport(): void {
    const project = this.projectService.currentProjectSig();
    if (!project) return;

    const allExportFields = [
      'Date',
      'Meeting Project',
      'Meeting Topic',
      'Resource',
      'User',
      'User Email',
      'Action',
      'Field',
      'From',
      'To',
      'Registrant Name',
      'Registrant Email',
      'Attendee Name',
      'Attendee Email',
      'Invitee Name',
      'Invitee Email',
    ];

    const filteredTableData = this.getFilteredAuditLogTableData();
    const columnMap = getColumnMap(this.datePipe);
    const exportFields = getAvailableExportFields(allExportFields, filteredTableData, columnMap);
    const fieldOptions = exportFields.map((field) => ({ label: field, value: field, checked: true }));

    this.dialogService
      .open(ExportDataComponent, {
        header: 'Export Meeting Audit Log',
        dismissableMask: true,
        data: {
          projectId: project.ID,
          action: `export-${project.Slug}${this.meetingTopic() ? `-${this.meetingTopic()}` : ''}-audit-log`,
          fieldOptions,
          project,
        },
      })
      ?.onClose.pipe(take(1))
      .subscribe((fields) => {
        if (fields && fields.length > 0) {
          this.exportAuditLog(fields, filteredTableData);
        }
      });
  }

  public toggleFilters(): void {
    this.isFiltersVisible.update((v) => !v);
  }

  public resetPage(): void {
    this.currentPage.set(0);
  }

  public onPageChange(event: PaginatorState): void {
    this.currentPage.set(event.page ?? 0);
    this.pageSize.set(event.rows ?? 50);
    this.scrollToTop();
  }

  public onCopyToClipboard(text: string): void {
    copyToClipboard(text)
      .then(() => {
        this.messageService.add({ severity: 'success', summary: 'Copied', detail: 'Successfully copied to clipboard', life: 3000 });
      })
      .catch(() => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to copy to clipboard', life: 3000 });
      });
  }

  private initializeAuditState(): Signal<AuditState> {
    return toSignal(
      toObservable(this.auditParams).pipe(
        switchMap(({ id, isPast }) => {
          if (!id) return of<AuditState>({ loading: false, error: false, data: null });

          if (!isPast) {
            return this.fetchAuditLogs({ meeting_id: id }).pipe(
              map(
                (logs): AuditState => ({
                  loading: false,
                  error: false,
                  data: logs.length ? processAuditLogs(logs, this.datePipe) : null,
                })
              ),
              catchError((error) => this.handleAuditError(error)),
              startWith<AuditState>({ loading: true, error: false, data: null })
            );
          }

          // Past meeting: fetch past-meeting logs, then also fetch original meeting logs and merge
          return this.fetchAuditLogs({ past_meeting_id: id }).pipe(
            switchMap((pastLogs) => {
              const meetingId = pastLogs.find((log) => log.past_meeting?.meeting_id)?.past_meeting?.meeting_id;
              if (!meetingId) return of(pastLogs);

              return this.fetchAuditLogs({ meeting_id: meetingId }).pipe(
                map((meetingLogs) => [...pastLogs, ...meetingLogs]),
                catchError(() => of(pastLogs))
              );
            }),
            map(
              (mergedLogs): AuditState => ({
                loading: false,
                error: false,
                data: mergedLogs.length ? processAuditLogs(mergedLogs, this.datePipe) : null,
              })
            ),
            catchError((error) => this.handleAuditError(error)),
            startWith<AuditState>({ loading: true, error: false, data: null })
          );
        })
      ),
      { initialValue: { loading: false, error: false, data: null } }
    );
  }

  private fetchAuditLogs(queryParams: Record<string, string>): Observable<MeetingAuditLog[]> {
    const params = new HttpParams({ fromObject: { ...queryParams, per_page: '1000' } });
    return this.itxService.getMeetingAuditLogs(params).pipe(map((response) => response?.data ?? []));
  }

  private handleAuditError(error: unknown): Observable<AuditState> {
    console.error('Failed to load meeting audit log:', error);
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to get meeting audit log. Please try again or contact support.',
      data: error,
      key: 'support',
    });
    return of<AuditState>({ loading: false, error: true, data: null });
  }

  private initializeFilteredAuditLogs(): Signal<ProcessedMeetingAuditLog[]> {
    return computed(() => {
      const data = this.auditData();
      if (!data) return [];
      const filterState: FilterState = {
        dateFrom: this.dateFrom(),
        dateTo: this.dateTo(),
        selectedAttributes: this.selectedAttributes(),
        selectedUsers: this.selectedUsers(),
        selectedActions: this.selectedActions(),
        selectedResources: this.selectedResources(),
        selectedRegistrants: this.selectedRegistrants(),
        selectedAttendees: this.selectedAttendees(),
      };
      return applyFilters(data.sortedAuditLogs, filterState);
    });
  }

  private initializeHasActiveFilters(): Signal<boolean> {
    return computed(() => {
      return !!(
        this.dateFrom() ||
        this.dateTo() ||
        this.selectedAttributes().length > 0 ||
        this.selectedResources().length > 0 ||
        this.selectedUsers().length > 0 ||
        this.selectedActions().length > 0 ||
        this.selectedRegistrants().length > 0 ||
        this.selectedAttendees().length > 0
      );
    });
  }

  private initializeActiveFilterCount(): Signal<number> {
    return computed(() => {
      let count = 0;
      if (this.dateFrom() || this.dateTo()) count++;
      count += this.selectedAttributes().length;
      count += this.selectedResources().length;
      count += this.selectedUsers().length;
      count += this.selectedActions().length;
      count += this.selectedRegistrants().length;
      count += this.selectedAttendees().length;
      return count;
    });
  }

  private scrollToTop(): void {
    this.scrollContainer()?.nativeElement?.scrollTo({ top: 0, behavior: 'smooth' });
  }

  private getFilteredAuditLogTableData(): MeetingAuditTableData[] {
    const filteredLogIds = new Set(this.filteredAuditLogs().map((log) => log.id));
    return this.auditLogTableData().filter((row) => filteredLogIds.has(row.logId));
  }

  private exportAuditLog(selectedColumns: string[], filteredTableData: MeetingAuditTableData[]): void {
    const columnMap = getColumnMap(this.datePipe);
    filteredTableData = [...filteredTableData].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    const data = filteredTableData.map((log) => selectedColumns.map((column) => columnMap[column]?.(log) || '—'));
    const slug = this.projectService.currentProjectSig()?.Slug ?? 'project';
    const filename = `${slug}${this.meetingTopic() ? `-${this.meetingTopic()}` : ''}-audit-log`;
    exportToCSV(data, filename, selectedColumns);
  }
}
